<template>
  <section id="product-batch-basic-table">
    <b-table
      :data="items"
      :mobile-cards="false"
      narrowed
      sticky-header
      :row-class="rowClass"
      :height="height"
    >
      <b-table-column label="" width="40" centered v-slot="props">
        <b-tooltip v-show="props.row.error" position="is-right">
          <b-icon icon="information" type="is-danger"></b-icon>
          <template v-slot:content>
            {{ props.row.error }}
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.product_code')"
        width="100"
        centered
        sortable
        v-slot="props"
      >
        <b-tooltip type="is-light" position="is-right">
          <p>{{ props.row.code }}</p>
          <template v-slot:content>
            <p v-html="props.row.name"></p>
          </template>
        </b-tooltip>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.shelve_status')"
        width="80"
        centered
        v-slot="props"
      >
        <b-switch
          :ref="`shelve-input-${props.row.code}`"
          v-model="props.row.shelve_on"
          :disabled="props.row.is_deprecated"
          @input="modified(props.row)"
        ></b-switch>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.situation')"
        width="130"
        centered
        v-slot="props"
      >
        <b-select
          :ref="`situation-input-${props.row.code}`"
          v-model="props.row.situation"
          @input="modified(props.row)"
        >
          <option
            v-for="option in situationTypes"
            :key="option.id"
            :value="option.id"
          >
            <!-- eslint-disable-next-line -->
          <p v-html="option.name"></p>
          </option>
        </b-select>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.airline')"
        width="320"
        centered
        v-slot="props"
      >
        <b-autocomplete
          :ref="`airline-input-${props.row.code}`"
          v-model="props.row.airlineValue"
          :data="props.row.airlines"
          field="display_name"
          :placeholder="$t('message.please_input') + $t('attributes.airline')"
          :open-on-focus="true"
          @typing="search => onSelectingAirline(props.row, search)"
          @select="option => onSelectedAirline(props.row, option)"
        >
          <template #default="air">
            <div class="media">
              <div class="media-left" style="width: 20px">
                {{ air.option.code }}
              </div>
              <div class="media-content">
                {{ air.option.name }}
                ({{ air.option.english_name }})
              </div>
            </div>
          </template>
          <template #empty>
            No results found
          </template>
        </b-autocomplete>
      </b-table-column>

      <b-table-column :label="$t('attributes.product_alias')" v-slot="props">
        <b-input
          :ref="`product-alias-input-${props.row.code}`"
          v-model="props.row.alias"
          @keypress.enter.exact.native="
            finishText(props.row, props.index + 1, 'product-alias')
          "
          @keypress.enter.shift.exact.native="
            finishText(props.row, props.index - 1, 'product-alias')
          "
          @input="modified(props.row)"
        />
      </b-table-column>

      <b-table-column :label="$t('attributes.operator_remark')" v-slot="props">
        <b-input
          :ref="`remark-input-${props.row.code}`"
          v-model="props.row.remark5"
          @keypress.enter.exact.native="
            finishText(props.row, props.index + 1, 'remark')
          "
          @keypress.enter.shift.exact.native="
            finishText(props.row, props.index - 1, 'remark')
          "
          @input="modified(props.row)"
        />
      </b-table-column>

      <b-table-column :label="$t('attributes.external_url')" v-slot="props">
        <b-input
          :ref="`external-url-input-${props.row.code}`"
          v-model="props.row.external_url"
          @keypress.enter.exact.native="
            finishText(props.row, props.index + 1, 'external-url')
          "
          @keypress.enter.shift.exact.native="
            finishText(props.row, props.index - 1, 'external-url')
          "
          @input="modified(props.row)"
        />
      </b-table-column>

      <b-table-column
        width="50"
        :label="$t('attributes.function')"
        centered
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-tooltip :label="$t('attributes.restore')">
            <b-button
              v-if="!props.row.editing"
              type="is-info"
              icon-left="restore"
              outlined
              @click.prevent="restore(props.row)"
            >
            </b-button>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section" style="height: 100%">
          <div v-if="!loading" class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'ProductBatchBasicTable',

  props: {
    products: {
      Type: Array,
      required: true
    },
    airlines: {
      required: true,
      type: Array
    },
    situationTypes: {
      required: true,
      type: Array
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractProducts(this.products)
    }
  },
  watch: {
    products(list) {
      this.items = this.extractProducts(list)
    }
  },
  methods: {
    extractProducts(products) {
      return products.map(product => {
        const air = this.airlines.find(a => a.code === product.airline)
        return {
          code: product.code,
          name: product.name,

          shelve_on: product.shelve_on,
          situation: product.situation,
          alias: product.alias,
          remark5: product.remark5,
          external_url: product.external_url,
          airlines: this.airlines,
          airline: product.airline,
          airlineValue: air && air.display_name,

          dirty: false,
          error: null,
          origin: product
        }
      })
    },
    onSelectingAirline: debounce(function(product, search) {
      const filter = search.toUpperCase().trim()
      product.airlines = this.airlines.filter(airline =>
        airline.display_name.toUpperCase().includes(filter)
      )
    }, 500),
    onSelectedAirline(product, option) {
      product.airline = option && option.code.trim()
      this.modified(product)
    },
    finishText(product, next, field) {
      this.modified(product)

      let index = (next + this.items.length) % this.items.length
      if (index >= 0 && index < this.items.length) {
        const nextProduct = this.items[index]

        setTimeout(() => {
          const name = `${field}-input-${nextProduct.code}`
          const textarea = this.$refs[name].getElement()
          if (textarea) {
            textarea.selectionStart = 0
            textarea.selectionEnd = 0
            textarea.focus()
          }
        }, 0)
      }
    },
    modified(product) {
      product.dirty =
        product.shelve_on !== product.origin.shelve_on ||
        product.situation !== product.origin.situation ||
        product.alias !== product.origin.alias ||
        product.remark5 !== product.origin.remark5 ||
        product.external_url !== product.origin.external_url ||
        product.airline !== product.origin.airline

      this.validate(product)
    },
    restore(product) {
      product.shelve_on = product.origin.shelve_on
      product.situation = product.origin.situation
      product.alias = product.origin.alias
      product.remark5 = product.origin.remark5
      product.external_url = product.origin.external_url

      const air = this.airlines.find(a => a.code === product.origin.airline)
      this.$refs[`airline-input-${product.code}`].setSelected(air)

      product.dirty = false
      product.error = false
    },
    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const product of this.items) {
        if (!this.validate(product)) {
          return false
        }
      }

      return true
    },
    validate(product) {
      if (!product.airline) {
        product.error = '航空公司必填'
        return false
      }

      product.error = null
      return true
    },
    getData() {
      return this.items
        .filter(product => product.dirty)
        .map(product => ({
          code: product.code,
          shelve_on: product.shelve_on,
          alias: product.alias,
          remark5: product.remark5,
          external_url: product.external_url,
          situation: product.situation,
          airline: product.airline
        }))
    },
    rowClass(product) {
      if (product.error) {
        return 'has-background-danger-light'
      } else if (product.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>

<style lang="scss">
#product-batch-basic-table {
  .b-table .table {
    min-width: 1480px;
  }
}
</style>
